import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Table } from '@/types';

/**
 * The Visual Table Planner is the source of truth for table/seat assignments.
 *
 * It stores each floor plan in `tablePlans/{roomId}` and renumbers seats every time it loads a
 * plan (see {@link normaliseTablePlanSeatNumbers}). Those renumbered values are what the planner
 * shows on screen but are not written back until the next edit, so any consumer reading raw
 * `seatNumber` values from Firestore can disagree with the plan. The legacy `tablePlan/visualLayout`
 * and `tablePlan/layout` documents are no longer written; they only held whichever floor plan was
 * saved last and are read solely for events planned before floor plans existed.
 *
 * Consumers that print table/seat details (place cards, attendance list, kitchen report) must load
 * via this module.
 */

type SeatNumberPriorityTable = { seatNumberPriority?: number; seating?: Array<{ seatNumber: number }> };

/**
 * Seat numbers run continuously across a floor plan's tables, ordered by `seatNumberPriority`
 * (lower first; ties keep their stored order). Mirrors the planner's own numbering exactly.
 */
export function recalculateSeatNumbers<T extends SeatNumberPriorityTable>(tables: T[]): T[] {
  const sortedTables = [...tables].sort((a, b) => {
    const priorityA = a.seatNumberPriority || 999;
    const priorityB = b.seatNumberPriority || 999;
    return priorityA - priorityB;
  });

  let globalSeatNumber = 1;

  return sortedTables.map((table) => ({
    ...table,
    seating: (table.seating ?? []).map((seat) => ({
      ...seat,
      seatNumber: globalSeatNumber++,
    })),
  }));
}

/** Applies the planner's load-time numbering to a floor plan's raw stored tables. */
export function normaliseTablePlanSeatNumbers<T extends SeatNumberPriorityTable>(rawTables: T[]): T[] {
  const withPriority = (rawTables || []).map((table, index) => ({
    ...table,
    seatNumberPriority: table.seatNumberPriority || index + 1,
  }));
  return recalculateSeatNumbers(withPriority);
}

type PlanDoc = { roomId: string; tables: unknown[] };

/** Floor plans (rooms) that exist for the event, or null when the event has none. */
type RoomInfo = { ids: Set<string>; names: Map<string, string> } | null;

type LoadOptions = {
  /** Bypass the offline cache (the kitchen report wants the latest seating). */
  fromServer?: boolean;
};

function activePlans(rooms: RoomInfo, plans: PlanDoc[]): PlanDoc[] {
  return plans.filter((plan) => !rooms || rooms.ids.has(plan.roomId));
}

/**
 * The legacy single-plan documents are only used for events planned before floor plans existed.
 * Once any floor plan exists they are ignored: they were a mirror of whichever plan was saved last
 * and can hold seating from other or deleted plans.
 */
function usesLegacyLayout(rooms: RoomInfo, plans: PlanDoc[]): boolean {
  return !rooms && plans.length === 0;
}

function combineRoomTables(rooms: RoomInfo, plans: PlanDoc[]): Table[] {
  return activePlans(rooms, plans).flatMap((plan) => normaliseTablePlanSeatNumbers(plan.tables as Table[]));
}

async function loadRooms(unitId: string, eventId: string, options: LoadOptions = {}): Promise<RoomInfo> {
  try {
    const ref = collection(db, 'units', unitId, 'events', eventId, 'rooms');
    const roomsSnap = options.fromServer ? await getDocsFromServer(ref) : await getDocs(ref);
    if (roomsSnap.empty) {
      return null;
    }
    return {
      ids: new Set(roomsSnap.docs.map((d) => d.id)),
      names: new Map(
        roomsSnap.docs.map((d) => [d.id, String((d.data() as { name?: unknown }).name ?? '').trim()])
      ),
    };
  } catch {
    return null;
  }
}

async function loadPlanDocs(unitId: string, eventId: string, options: LoadOptions = {}): Promise<PlanDoc[]> {
  const ref = collection(db, 'units', unitId, 'events', eventId, 'tablePlans');
  const plansSnap = options.fromServer ? await getDocsFromServer(ref) : await getDocs(ref);
  return plansSnap.docs.map((d) => ({
    roomId: d.id,
    tables: Array.isArray(d.data().tables) ? d.data().tables : [],
  }));
}

async function loadLegacyLayout(
  unitId: string,
  eventId: string,
  options: LoadOptions = {}
): Promise<{ tables: Table[]; name: string | null }> {
  for (const docId of ['visualLayout', 'layout']) {
    const ref = doc(db, 'units', unitId, 'events', eventId, 'tablePlan', docId);
    const snap = options.fromServer ? await getDocFromServer(ref) : await getDoc(ref);
    const data = snap.exists() ? (snap.data() as { tables?: Table[]; name?: unknown }) : undefined;
    if (Array.isArray(data?.tables) && data.tables.length > 0) {
      const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : null;
      return { tables: normaliseTablePlanSeatNumbers(data.tables), name };
    }
  }
  return { tables: [], name: null };
}

async function loadLegacyTables(unitId: string, eventId: string): Promise<Table[]> {
  return (await loadLegacyLayout(unitId, eventId)).tables;
}

/**
 * Loads every floor plan's tables for an event with seat numbers matching the planner.
 * Falls back to the legacy single-plan documents for events planned before floor plans existed.
 */
export async function loadEventTablePlanTables(unitId: string, eventId: string): Promise<Table[]> {
  const [rooms, plans] = await Promise.all([loadRooms(unitId, eventId), loadPlanDocs(unitId, eventId)]);
  if (usesLegacyLayout(rooms, plans)) {
    return loadLegacyTables(unitId, eventId);
  }
  return combineRoomTables(rooms, plans);
}

export type SeatAssignment = { roomName: string | null; tableName: string | null; seatNumber: number | null };

/**
 * Where each guest (by RSVP id) is seated, with the floor plan's name and the seat number shown in
 * the planner. Same sources and legacy fallback as {@link loadEventTablePlanTables}.
 */
export async function loadEventSeatAssignments(
  unitId: string,
  eventId: string,
  options: LoadOptions = {}
): Promise<Record<string, SeatAssignment>> {
  const [rooms, plans] = await Promise.all([
    loadRooms(unitId, eventId, options),
    loadPlanDocs(unitId, eventId, options),
  ]);
  const assignments: Record<string, SeatAssignment> = {};

  const addTables = (tables: Table[], roomName: string | null) => {
    tables.forEach((table, tableIndex) => {
      const tableName =
        typeof table?.name === 'string' && table.name.trim() ? table.name.trim() : `Table ${tableIndex + 1}`;
      (Array.isArray(table?.seating) ? table.seating : []).forEach((seat) => {
        if (seat?.rsvpId && !assignments[seat.rsvpId]) {
          assignments[seat.rsvpId] = {
            roomName,
            tableName,
            seatNumber: typeof seat.seatNumber === 'number' ? seat.seatNumber : null,
          };
        }
      });
    });
  };

  if (usesLegacyLayout(rooms, plans)) {
    const legacy = await loadLegacyLayout(unitId, eventId, options);
    addTables(legacy.tables, legacy.name ?? 'Main Dining Room');
    return assignments;
  }

  activePlans(rooms, plans).forEach((plan, planIndex) => {
    const roomName = rooms?.names.get(plan.roomId) || `Room ${planIndex + 1}`;
    addTables(normaliseTablePlanSeatNumbers(plan.tables as Table[]), roomName);
  });
  return assignments;
}

/**
 * Live version of {@link loadEventTablePlanTables}: re-emits whenever any floor plan changes.
 */
export function subscribeEventTablePlanTables(
  unitId: string,
  eventId: string,
  onTables: (tables: Table[]) => void,
  onError?: (error: unknown) => void
): Unsubscribe {
  let cancelled = false;
  let latestSnapshot = 0;

  const unsubscribe = onSnapshot(
    collection(db, 'units', unitId, 'events', eventId, 'tablePlans'),
    (snapshot) => {
      const snapshotId = ++latestSnapshot;
      const plans = snapshot.docs.map((d) => ({
        roomId: d.id,
        tables: Array.isArray(d.data().tables) ? d.data().tables : [],
      }));
      // Rooms can be added/removed while the page is open; refresh alongside plan changes.
      void loadRooms(unitId, eventId)
        .then(async (rooms) => {
          const resolved = usesLegacyLayout(rooms, plans)
            ? await loadLegacyTables(unitId, eventId)
            : combineRoomTables(rooms, plans);
          if (!cancelled && snapshotId === latestSnapshot) {
            onTables(resolved);
          }
        })
        .catch((error) => onError?.(error));
    },
    (error) => onError?.(error)
  );

  return () => {
    cancelled = true;
    unsubscribe();
  };
}
